<script setup lang="ts">
import { useModalStore } from '@/shared/ui/AppModal/store/modal';
import { LAWYER_INFO } from '@/pages/lawyer/model/keys';
import type { ILawyerLegalService } from '@/entities/lawyer/model/types/lawyer';

import { FREE_LEGAL_SERVICE_ID } from '@/shared/constants/lawyerServiceConstants';

interface ServiceRow {
  id: number;
  name: string;
  cost: string;
  note?: string;
}

const isMobile: boolean = useIsMobile();
const lawyerInfo = inject(LAWYER_INFO);
const modalStore = useModalStore();

const openCallback = (): void => {
  modalStore.setModalState('callback', true, {
    lawyerInfo: lawyerInfo?.value ?? null,
    formTitle: 'Рассчитайте стоимость услуг по своей ситуации',
  });
};

const formatPrice = (value?: number): string => (value ?? 0).toLocaleString('ru');

const costRange = (service: ILawyerLegalService): string =>
  `от ${formatPrice(service.cost_min)} до ${formatPrice(service.cost_max)} ₽`;

const services: ComputedRef<ServiceRow[]> = computed(() => {
  const legalServices = lawyerInfo?.value?.legalServices ?? [];
  const free = legalServices.find((s) => s.service_id === FREE_LEGAL_SERVICE_ID);

  if (!free) {
    return legalServices.map((service) => ({
      id: service.id,
      name: service.name,
      cost: costRange(service),
    }));
  }

  const rows: ServiceRow[] = [
    { id: free.id, name: free.name, cost: `0 ₽ за ${free.period} мин.` },
  ];

  legalServices
    .filter((service) => service.service_id !== FREE_LEGAL_SERVICE_ID)
    .forEach((service, index) => {
      rows.push({
        id: service.id,
        name: service.name,
        cost: costRange(service),
        note: index === 0 ? `Первые ${free.period} мин. бесплатно` : undefined,
      });
    });

  return rows;
});
</script>

<template>
  <section id="services" :class="['lawyer-services-section', { _mobile: isMobile }]">
    <h2 class="lawyer-services-section__title">Цены на услуги</h2>
    <div v-if="services.length" class="lawyer-services-section__info">
      <ul class="lawyer-services-section__list">
        <li
          v-for="service in services"
          :key="service.id"
          class="lawyer-services-section__service"
        >
          <div class="lawyer-services-section__service-name">
            {{ service.name }}
            <span v-if="service.note" class="lawyer-services-section__service-note">
              {{ service.note }}
            </span>
          </div>
          <div class="lawyer-services-section__service-cost">{{ service.cost }}</div>
        </li>
      </ul>
      <div class="lawyer-services-section__action">
        <AppButton
          class="lawyer-services-section__button"
          is-big
          sup-text="0&#8381;"
          button-text="Рассчитать стоимость"
          is-hover-pulse-effect
          @click="openCallback"
        />
      </div>
    </div>
    <div v-else class="lawyer-services-section__empty">
      Автор профиля пока не указал цены на услуги
    </div>
  </section>
</template>

<style lang="less">
.lawyer-services-section {
  &__title {
    font-weight: 600;
    font-size: @size-x6;
    line-height: 25px;
    margin: 0 0 @size-x5;
  }

  &__info {
    .box-shadow();

    background: #fff;
    border-radius: @main-size;
    padding: 42px 30px 30px;
  }

  &__service {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: @size-x4;
    font-size: @size-x4;
    line-height: 22px;
    padding: @size-x2 0;

    &:not(:last-child) {
      border-bottom: 1px solid rgba(@color-dove-gray, 0.25);
    }
  }

  &__service-name {
    max-width: 350px;
  }

  &__service-note {
    color: @color-dove-gray;
    font-size: 12px;
    font-weight: 500;
    margin-left: @size-x2;
  }

  &__service-cost {
    font-weight: 700;
    text-align: right;
    white-space: nowrap;
  }

  &__action {
    display: flex;
    justify-content: flex-end;
    margin-top: @size-x8;
  }

  &__empty {
    font-weight: 500;
  }

  &._mobile &__title {
    margin-bottom: @size-x4;
  }

  &._mobile &__info {
    padding: @size-x4 @size-x4 @size-x6;
  }

  &._mobile &__service {
    font-size: 14px;
    line-height: 19px;
  }

  &._mobile &__service-name {
    max-width: 150px;
  }

  &._mobile &__action {
    justify-content: center;
  }

  &._mobile &__button {
    margin-left: auto;
  }
}
</style>
